import type { TooltipOptions, TooltipCallbacks, TooltipItem } from 'chart.js'
import { resolveCanvasColor } from '@/lib/charts/chartjs'

/**
 * A deep-partial of Chart.js `TooltipOptions`: every top-level field is
 * optional AND `callbacks` is itself a partial (Chart.js' own `TooltipOptions`
 * types every callback as required, but `options.plugins.tooltip` accepts a
 * deep-partial at runtime). Chart.js does not re-export its internal
 * `DeepPartial`, so we model just the shape we need.
 */
export type ChartTooltipPreset = Partial<Omit<TooltipOptions<any>, 'callbacks'>> & {
  callbacks?: Partial<TooltipCallbacks<any, any, TooltipItem<any>>>
}

/**
 * QS-47 — shared Chart.js tooltip preset.
 *
 * Chart.js ships a dark, low-contrast default tooltip that reads poorly on the
 * app's light deck theme, and its default `title`+`label` shape repeats the
 * series label twice (e.g. title "Yes" + body "Yes: 29"). This module provides
 * one theme-aware preset using the app's opaque tooltip surface
 * (`--tooltip` background, `--border` border, `--tooltip-foreground` text)
 * plus callbacks that render the value ONCE with context and drop the
 * redundant title row.
 *
 * Purely presentational: no data-shape changes. Shared across every Chart.js
 * chart in deck-ui (report/deck + Quick Study), so the styling is consistent.
 */

/** Color tokens for the tooltip surface, resolved to concrete canvas colors. */
export interface ChartTooltipPresetColors {
  /** Tooltip surface background. */
  background: string
  /** Tooltip border + the divider under the title (when shown). */
  border: string
  /** High-contrast body text. */
  body: string
  /** Muted title text (when a title row is shown). */
  title: string
}

/**
 * Resolve semantic tooltip colors into concrete
 * canvas-safe color strings. Resolved lazily at render time so theme changes
 * (light/dark, brand accent) are picked up.
 */
export function resolveChartTooltipColors(): ChartTooltipPresetColors {
  return {
    background: resolveCanvasColor('var(--tooltip)', '#ffffff'),
    border: resolveCanvasColor('var(--border)', '#e5e7eb'),
    body: resolveCanvasColor('var(--tooltip-foreground)', '#0f172a'),
    title: resolveCanvasColor('var(--tooltip-foreground)', '#64748b'),
  }
}

/**
 * Format a single tooltip row as "Label — value" so the value renders once with
 * its category as context (e.g. "Yes — 29 (57%)"). Returns just the label when
 * the value text is empty.
 */
export function formatTooltipRow(label: string | undefined, valueText: string | undefined): string {
  const safeLabel = (label ?? '').trim()
  const safeValue = (valueText ?? '').trim()
  if (!safeLabel) return safeValue
  if (!safeValue) return safeLabel
  return `${safeLabel}: ${safeValue}`
}

/**
 * The base theme-aware styling shared by every chart, independent of the
 * value-formatting callbacks. Opaque high-contrast surface, a soft border,
 * comfortable padding, and a color swatch matching the series.
 */
export function chartTooltipStyle(): ChartTooltipPreset {
  const colors = resolveChartTooltipColors()
  return {
    backgroundColor: colors.background,
    borderColor: colors.border,
    borderWidth: 1,
    titleColor: colors.title,
    bodyColor: colors.body,
    footerColor: colors.title,
    padding: 10,
    cornerRadius: 8,
    boxPadding: 4,
    // Series swatch reads as a color chip rather than a colored box outline.
    usePointStyle: true,
    displayColors: true,
    // Soft drop shadow off; the border + light surface is enough on a light deck.
    caretSize: 5,
  }
}

/**
 * Options accepted by {@link chartTooltipPreset}.
 */
export interface ChartTooltipPresetOptions {
  /**
   * When true (the default), the redundant title row is dropped — the series
   * label is folded into the single body row instead. Set false for charts
   * where the title is meaningful and distinct from the body (e.g. grouped bars
   * where the title is the shared category and each body row is a series).
   */
  dropTitle?: boolean
  /**
   * Render the value portion of a row for the single-series dedupe case
   * (doughnut/pie/bar). Receives the Chart.js tooltip item; return the value
   * text WITHOUT the label (e.g. "29 (57%)"). When omitted, the chart keeps its
   * own existing `callbacks.label` and only the surface styling is applied.
   */
  valueText?: (item: any) => string
}

/**
 * Build a complete `options.plugins.tooltip` config: the shared light-surface
 * styling, plus — when `valueText` is supplied — a `callbacks.label` that emits
 * the label + value ONCE and a `callbacks.title` that suppresses the duplicate
 * title row.
 *
 * Usage in a chart:
 *   tooltip: chartTooltipPreset({ valueText: (i) => `${Math.round(i.parsed)}%` })
 */
export function chartTooltipPreset(
  options: ChartTooltipPresetOptions = {}
): ChartTooltipPreset {
  const { dropTitle = true, valueText } = options
  const base = chartTooltipStyle()

  if (!valueText) {
    // Style-only: keep whatever callbacks the chart already defines.
    return dropTitle
      ? { ...base, callbacks: { title: () => '' } }
      : base
  }

  return {
    ...base,
    callbacks: {
      // Single body row: "Label — value", value rendered once with context.
      // When the title row is kept (grouped / stacked bars), `item.label` is
      // the shared category already shown as the title; the body names the series.
      label: (item: any) =>
        formatTooltipRow(
          dropTitle ? item?.label : (item?.dataset?.label ?? item?.label),
          valueText(item),
        ),
      // Drop the redundant title row (the label already appears in the body).
      ...(dropTitle ? { title: () => '' } : {}),
    },
  }
}
